/**
 * ProductTabs Web Component
 *
 * Implements accessible W3C WAI-ARIA Tabs pattern with progressive enhancement.
 * Transforms child accordion/details elements into seamless horizontal tabs,
 * falling back to accessible disclosure elements if JavaScript is unavailable.
 */
class ProductTabs extends HTMLElement {
  /** @type {HTMLButtonElement[]} */
  #tabs = [];
  /** @type {HTMLElement[]} */
  #panels = [];

  connectedCallback() {
    this.#initTabs();
  }

  #initTabs() {
    const rawTabs = Array.from(this.querySelectorAll('[role="tab"]'));
    const rawPanels = Array.from(this.querySelectorAll('.product-tabs__panels > *'));

    if (rawTabs.length === 0 || rawPanels.length === 0) {
      return;
    }

    const validTabs = [];
    const validPanels = [];

    rawTabs.forEach((tab, index) => {
      const panelId = tab.getAttribute('aria-controls');
      const panel = (panelId ? this.querySelector(`#${panelId}`) : null) || rawPanels[index];

      // Check if panel is empty
      const isEmpty = !panel ||
        panel.hasAttribute('data-tab-empty') ||
        panel.classList.contains('product-tabs__panel--empty');

      if (isEmpty) {
        tab.hidden = true;
        tab.style.display = 'none';
        tab.classList.add('is-hidden');
        if (panel) {
          panel.hidden = true;
          panel.style.display = 'none';
        }
        return;
      }

      validTabs.push(tab);
      validPanels.push(panel);
    });

    this.#tabs = validTabs;
    this.#panels = validPanels;

    if (this.#tabs.length === 0) {
      this.hidden = true;
      this.style.display = 'none';
      return;
    }

    // Clean up separators if any tabs were dynamically hidden
    const separators = Array.from(this.querySelectorAll('.product-tabs__separator'));
    separators.forEach((sep) => sep.remove());

    // Associate tabs with panels and setup accessibility attributes
    this.#tabs.forEach((tab, index) => {
      const panel = this.#panels[index];
      const panelId = tab.getAttribute('aria-controls') || `panel-${tab.id || index}`;
      tab.setAttribute('aria-controls', panelId);

      panel.id = panelId;
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('tabindex', '0');
      panel.setAttribute('aria-labelledby', tab.id);

      // Re-insert separator after tab if not the last visible tab
      if (index < this.#tabs.length - 1) {
        const separator = document.createElement('span');
        separator.className = 'product-tabs__separator';
        separator.setAttribute('aria-hidden', 'true');
        separator.textContent = '•';
        tab.after(separator);
      }

      // Open details if enclosed
      const details = panel.querySelector('details');
      if (details) {
        details.open = true;
        const resolvedHeading = details.getAttribute('data-tab-heading');
        const tabTextEl = tab.querySelector('.product-tabs__tab-text');
        if (resolvedHeading && tabTextEl) {
          tabTextEl.textContent = resolvedHeading;
        }
      }

      tab.addEventListener('click', (e) => {
        e.preventDefault();
        this.selectTab(index);
      });

      tab.addEventListener('keydown', (e) => this.#handleKeyDown(e, index));
    });

    // Determine initial active index (default to 0 or any with aria-selected="true")
    let activeIndex = this.#tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    if (activeIndex === -1) {
      activeIndex = 0;
    }

    this.selectTab(activeIndex, false);
  }

  /**
   * Selects a tab by index and updates active states
   * @param {number} index
   * @param {boolean} [shouldFocus=true]
   */
  selectTab(index, shouldFocus = true) {
    if (index < 0 || index >= this.#tabs.length) return;

    this.#tabs.forEach((tab, i) => {
      const isSelected = i === index;
      tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      tab.setAttribute('tabindex', isSelected ? '0' : '-1');
      tab.classList.toggle('is-active', isSelected);

      const panel = this.#panels[i];
      if (panel) {
        panel.classList.toggle('is-active', isSelected);
        panel.hidden = !isSelected;
        const details = panel.querySelector('details');
        if (details) {
          details.open = isSelected;
        }
      }

      if (isSelected && shouldFocus) {
        tab.focus();
      }
    });
  }

  /**
   * Keyboard navigation per W3C WAI-ARIA Tabs pattern
   * @param {KeyboardEvent} event
   * @param {number} currentIndex
   */
  #handleKeyDown(event, currentIndex) {
    const count = this.#tabs.length;
    let targetIndex = -1;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        targetIndex = (currentIndex + 1) % count;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        targetIndex = (currentIndex - 1 + count) % count;
        break;
      case 'Home':
        event.preventDefault();
        targetIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        targetIndex = count - 1;
        break;
      default:
        return;
    }

    if (targetIndex !== -1) {
      this.selectTab(targetIndex, true);
    }
  }
}

if (!customElements.get('product-tabs')) {
  customElements.define('product-tabs', ProductTabs);
}
